/**
 * SDFEngine.ts
 * CPU-based Signed Distance Field engine for CNC stock simulation
 *
 * Uses float distance field where:
 * - Negative values = inside material
 * - Positive values = outside material
 * - Zero = surface boundary
 *
 * Provides MUCH smoother surfaces than binary voxel MC due to proper
 * distance-based vertex interpolation.
 */

import * as THREE from 'three';
import { VoxelToolGeometry, VoxelBBox } from './VoxelCSGEngine';

// Marching Cubes lookup tables
const MC_EDGE_TABLE = new Uint16Array([
  0x0, 0x109, 0x203, 0x30a, 0x406, 0x50f, 0x605, 0x70c,
  0x80c, 0x905, 0xa0f, 0xb06, 0xc0a, 0xd03, 0xe09, 0xf00,
  0x190, 0x99, 0x393, 0x29a, 0x596, 0x49f, 0x795, 0x69c,
  0x99c, 0x895, 0xb9f, 0xa96, 0xd9a, 0xc93, 0xf99, 0xe90,
  0x230, 0x339, 0x33, 0x13a, 0x636, 0x73f, 0x435, 0x53c,
  0xa3c, 0xb35, 0x83f, 0x936, 0xe3a, 0xf33, 0xc39, 0xd30,
  0x3a0, 0x2a9, 0x1a3, 0xaa, 0x7a6, 0x6af, 0x5a5, 0x4ac,
  0xbac, 0xaa5, 0x9af, 0x8a6, 0xfaa, 0xea3, 0xda9, 0xca0,
  0x460, 0x569, 0x663, 0x76a, 0x66, 0x16f, 0x265, 0x36c,
  0xc6c, 0xd65, 0xe6f, 0xf66, 0x86a, 0x963, 0xa69, 0xb60,
  0x5f0, 0x4f9, 0x7f3, 0x6fa, 0x1f6, 0xff, 0x3f5, 0x2fc,
  0xdfc, 0xcf5, 0xfff, 0xef6, 0x9fa, 0x8f3, 0xbf9, 0xaf0,
  0x650, 0x759, 0x453, 0x55a, 0x256, 0x35f, 0x55, 0x15c,
  0xe5c, 0xf55, 0xc5f, 0xd56, 0xa5a, 0xb53, 0x859, 0x950,
  0x7c0, 0x6c9, 0x5c3, 0x4ca, 0x3c6, 0x2cf, 0x1c5, 0xcc,
  0xfcc, 0xec5, 0xdcf, 0xcc6, 0xbca, 0xac3, 0x9c9, 0x8c0,
  0x8c0, 0x9c9, 0xac3, 0xbca, 0xcc6, 0xdcf, 0xec5, 0xfcc,
  0xcc, 0x1c5, 0x2cf, 0x3c6, 0x4ca, 0x5c3, 0x6c9, 0x7c0,
  0x950, 0x859, 0xb53, 0xa5a, 0xd56, 0xc5f, 0xf55, 0xe5c,
  0x15c, 0x55, 0x35f, 0x256, 0x55a, 0x453, 0x759, 0x650,
  0xaf0, 0xbf9, 0x8f3, 0x9fa, 0xef6, 0xfff, 0xcf5, 0xdfc,
  0x2fc, 0x3f5, 0xff, 0x1f6, 0x6fa, 0x7f3, 0x4f9, 0x5f0,
  0xb60, 0xa69, 0x963, 0x86a, 0xf66, 0xe6f, 0xd65, 0xc6c,
  0x36c, 0x265, 0x16f, 0x66, 0x76a, 0x663, 0x569, 0x460,
  0xca0, 0xda9, 0xea3, 0xfaa, 0x8a6, 0x9af, 0xaa5, 0xbac,
  0x4ac, 0x5a5, 0x6af, 0x7a6, 0xaa, 0x1a3, 0x2a9, 0x3a0,
  0xd30, 0xc39, 0xf33, 0xe3a, 0x936, 0x83f, 0xb35, 0xa3c,
  0x53c, 0x435, 0x73f, 0x636, 0x13a, 0x33, 0x339, 0x230,
  0xe90, 0xf99, 0xc93, 0xd9a, 0xa96, 0xb9f, 0x895, 0x99c,
  0x69c, 0x795, 0x49f, 0x596, 0x29a, 0x393, 0x99, 0x190,
  0xf00, 0xe09, 0xd03, 0xc0a, 0xb06, 0xa0f, 0x905, 0x80c,
  0x70c, 0x605, 0x50f, 0x406, 0x30a, 0x203, 0x109, 0x0
]);

const MC_TRI_TABLE = [
  [], [0, 8, 3], [0, 1, 9], [1, 8, 3, 9, 8, 1],
  [1, 2, 10], [0, 8, 3, 1, 2, 10], [9, 2, 10, 0, 2, 9],
  [2, 8, 3, 2, 10, 8, 10, 9, 8], [3, 11, 2], [0, 11, 2, 8, 11, 0],
  [1, 9, 0, 2, 3, 11], [1, 11, 2, 1, 9, 11, 9, 8, 11],
  [3, 10, 1, 11, 10, 3], [0, 10, 1, 0, 8, 10, 8, 11, 10],
  [3, 9, 0, 3, 11, 9, 11, 10, 9], [9, 8, 10, 10, 8, 11],
  [4, 7, 8], [4, 3, 0, 7, 3, 4], [0, 1, 9, 8, 4, 7],
  [4, 1, 9, 4, 7, 1, 7, 3, 1], [1, 2, 10, 8, 4, 7],
  [3, 4, 7, 3, 0, 4, 1, 2, 10], [9, 2, 10, 9, 0, 2, 8, 4, 7],
  [2, 10, 9, 2, 9, 7, 2, 7, 3, 7, 9, 4], [8, 4, 7, 3, 11, 2],
  [11, 4, 7, 11, 2, 4, 2, 0, 4], [9, 0, 1, 8, 4, 7, 2, 3, 11],
  [4, 7, 11, 9, 4, 11, 9, 11, 2, 9, 2, 1], [3, 10, 1, 3, 11, 10, 7, 8, 4],
  [1, 11, 10, 1, 4, 11, 1, 0, 4, 7, 11, 4], [4, 7, 8, 9, 0, 11, 9, 11, 10, 11, 0, 3],
  [4, 7, 11, 4, 11, 9, 9, 11, 10], [9, 5, 4], [9, 5, 4, 0, 8, 3],
  [0, 5, 4, 1, 5, 0], [8, 5, 4, 8, 3, 5, 3, 1, 5],
  [1, 2, 10, 9, 5, 4], [3, 0, 8, 1, 2, 10, 4, 9, 5],
  [5, 2, 10, 5, 4, 2, 4, 0, 2], [2, 10, 5, 3, 2, 5, 3, 5, 4, 3, 4, 8],
  [9, 5, 4, 2, 3, 11], [0, 11, 2, 0, 8, 11, 4, 9, 5],
  [0, 5, 4, 0, 1, 5, 2, 3, 11], [2, 1, 5, 2, 5, 8, 2, 8, 11, 4, 8, 5],
  [10, 3, 11, 10, 1, 3, 9, 5, 4], [4, 9, 5, 0, 8, 1, 8, 10, 1, 8, 11, 10],
  [5, 4, 0, 5, 0, 11, 5, 11, 10, 11, 0, 3], [5, 4, 8, 5, 8, 10, 10, 8, 11],
  [9, 7, 8, 5, 7, 9], [9, 3, 0, 9, 5, 3, 5, 7, 3],
  [0, 7, 8, 0, 1, 7, 1, 5, 7], [1, 5, 3, 3, 5, 7],
  [9, 7, 8, 9, 5, 7, 10, 1, 2], [10, 1, 2, 9, 5, 0, 5, 3, 0, 5, 7, 3],
  [8, 0, 2, 8, 2, 5, 8, 5, 7, 10, 5, 2], [2, 10, 5, 2, 5, 3, 3, 5, 7],
  [7, 9, 5, 7, 8, 9, 3, 11, 2], [9, 5, 7, 9, 7, 2, 9, 2, 0, 2, 7, 11],
  [2, 3, 11, 0, 1, 8, 1, 7, 8, 1, 5, 7], [11, 2, 1, 11, 1, 7, 7, 1, 5],
  [9, 5, 8, 8, 5, 7, 10, 1, 3, 10, 3, 11], [5, 7, 0, 5, 0, 9, 7, 11, 0, 1, 0, 10, 11, 10, 0],
  [11, 10, 0, 11, 0, 3, 10, 5, 0, 8, 0, 7, 5, 7, 0], [11, 10, 5, 7, 11, 5],
  [10, 6, 5], [0, 8, 3, 5, 10, 6], [9, 0, 1, 5, 10, 6],
  [1, 8, 3, 1, 9, 8, 5, 10, 6], [1, 6, 5, 2, 6, 1],
  [1, 6, 5, 1, 2, 6, 3, 0, 8], [9, 6, 5, 9, 0, 6, 0, 2, 6],
  [5, 9, 8, 5, 8, 2, 5, 2, 6, 3, 2, 8], [2, 3, 11, 10, 6, 5],
  [11, 0, 8, 11, 2, 0, 10, 6, 5], [0, 1, 9, 2, 3, 11, 5, 10, 6],
  [5, 10, 6, 1, 9, 2, 9, 11, 2, 9, 8, 11], [6, 3, 11, 6, 5, 3, 5, 1, 3],
  [0, 8, 11, 0, 11, 5, 0, 5, 1, 5, 11, 6], [3, 11, 6, 0, 3, 6, 0, 6, 5, 0, 5, 9],
  [6, 5, 9, 6, 9, 11, 11, 9, 8], [5, 10, 6, 4, 7, 8],
  [4, 3, 0, 4, 7, 3, 6, 5, 10], [1, 9, 0, 5, 10, 6, 8, 4, 7],
  [10, 6, 5, 1, 9, 7, 1, 7, 3, 7, 9, 4], [6, 1, 2, 6, 5, 1, 4, 7, 8],
  [1, 2, 5, 5, 2, 6, 3, 0, 4, 3, 4, 7], [8, 4, 7, 9, 0, 5, 0, 6, 5, 0, 2, 6],
  [7, 3, 9, 7, 9, 4, 3, 2, 9, 5, 9, 6, 2, 6, 9], [3, 11, 2, 7, 8, 4, 10, 6, 5],
  [5, 10, 6, 4, 7, 2, 4, 2, 0, 2, 7, 11], [0, 1, 9, 4, 7, 8, 2, 3, 11, 5, 10, 6],
  [9, 2, 1, 9, 11, 2, 9, 4, 11, 7, 11, 4, 5, 10, 6], [8, 4, 7, 3, 11, 5, 3, 5, 1, 5, 11, 6],
  [5, 1, 11, 5, 11, 6, 1, 0, 11, 7, 11, 4, 0, 4, 11], [0, 5, 9, 0, 6, 5, 0, 3, 6, 11, 6, 3, 8, 4, 7],
  [6, 5, 9, 6, 9, 11, 4, 7, 9, 7, 11, 9], [10, 4, 9, 6, 4, 10],
  [4, 10, 6, 4, 9, 10, 0, 8, 3], [10, 0, 1, 10, 6, 0, 6, 4, 0],
  [8, 3, 1, 8, 1, 6, 8, 6, 4, 6, 1, 10], [1, 4, 9, 1, 2, 4, 2, 6, 4],
  [3, 0, 8, 1, 2, 9, 2, 4, 9, 2, 6, 4], [0, 2, 4, 4, 2, 6],
  [8, 3, 2, 8, 2, 4, 4, 2, 6], [10, 4, 9, 10, 6, 4, 11, 2, 3],
  [0, 8, 2, 2, 8, 11, 4, 9, 10, 4, 10, 6], [3, 11, 2, 0, 1, 6, 0, 6, 4, 6, 1, 10],
  [6, 4, 1, 6, 1, 10, 4, 8, 1, 2, 1, 11, 8, 11, 1], [9, 6, 4, 9, 3, 6, 9, 1, 3, 11, 6, 3],
  [8, 11, 1, 8, 1, 0, 11, 6, 1, 9, 1, 4, 6, 4, 1], [3, 11, 6, 3, 6, 0, 0, 6, 4],
  [6, 4, 8, 11, 6, 8], [7, 10, 6, 7, 8, 10, 8, 9, 10],
  [0, 7, 3, 0, 10, 7, 0, 9, 10, 6, 7, 10], [10, 6, 7, 1, 10, 7, 1, 7, 8, 1, 8, 0],
  [10, 6, 7, 10, 7, 1, 1, 7, 3], [1, 2, 6, 1, 6, 8, 1, 8, 9, 8, 6, 7],
  [2, 6, 9, 2, 9, 1, 6, 7, 9, 0, 9, 3, 7, 3, 9], [7, 8, 0, 7, 0, 6, 6, 0, 2],
  [7, 3, 2, 6, 7, 2], [2, 3, 11, 10, 6, 8, 10, 8, 9, 8, 6, 7],
  [2, 0, 7, 2, 7, 11, 0, 9, 7, 6, 7, 10, 9, 10, 7], [1, 8, 0, 1, 7, 8, 1, 10, 7, 6, 7, 10, 2, 3, 11],
  [11, 2, 1, 11, 1, 7, 10, 6, 1, 6, 7, 1], [8, 9, 6, 8, 6, 7, 9, 1, 6, 11, 6, 3, 1, 3, 6],
  [0, 9, 1, 11, 6, 7], [7, 8, 0, 7, 0, 6, 3, 11, 0, 11, 6, 0],
  [7, 11, 6], [7, 6, 11], [3, 0, 8, 11, 7, 6],
  [0, 1, 9, 11, 7, 6], [8, 1, 9, 8, 3, 1, 11, 7, 6],
  [10, 1, 2, 6, 11, 7], [1, 2, 10, 3, 0, 8, 6, 11, 7],
  [2, 9, 0, 2, 10, 9, 6, 11, 7], [6, 11, 7, 2, 10, 3, 10, 8, 3, 10, 9, 8],
  [7, 2, 3, 6, 2, 7], [7, 0, 8, 7, 6, 0, 6, 2, 0],
  [2, 7, 6, 2, 3, 7, 0, 1, 9], [1, 6, 2, 1, 8, 6, 1, 9, 8, 8, 7, 6],
  [10, 7, 6, 10, 1, 7, 1, 3, 7], [10, 7, 6, 1, 7, 10, 1, 8, 7, 1, 0, 8],
  [0, 3, 7, 0, 7, 10, 0, 10, 9, 6, 10, 7], [7, 6, 10, 7, 10, 8, 8, 10, 9],
  [6, 8, 4, 11, 8, 6], [3, 6, 11, 3, 0, 6, 0, 4, 6],
  [8, 6, 11, 8, 4, 6, 9, 0, 1], [9, 4, 6, 9, 6, 3, 9, 3, 1, 11, 3, 6],
  [6, 8, 4, 6, 11, 8, 2, 10, 1], [1, 2, 10, 3, 0, 11, 0, 6, 11, 0, 4, 6],
  [4, 11, 8, 4, 6, 11, 0, 2, 9, 2, 10, 9], [10, 9, 3, 10, 3, 2, 9, 4, 3, 11, 3, 6, 4, 6, 3],
  [8, 2, 3, 8, 4, 2, 4, 6, 2], [0, 4, 2, 4, 6, 2],
  [1, 9, 0, 2, 3, 4, 2, 4, 6, 4, 3, 8], [1, 9, 4, 1, 4, 2, 2, 4, 6],
  [8, 1, 3, 8, 6, 1, 8, 4, 6, 6, 10, 1], [10, 1, 0, 10, 0, 6, 6, 0, 4],
  [4, 6, 3, 4, 3, 8, 6, 10, 3, 0, 3, 9, 10, 9, 3], [10, 9, 4, 6, 10, 4],
  [4, 9, 5, 7, 6, 11], [0, 8, 3, 4, 9, 5, 11, 7, 6],
  [5, 0, 1, 5, 4, 0, 7, 6, 11], [11, 7, 6, 8, 3, 4, 3, 5, 4, 3, 1, 5],
  [9, 5, 4, 10, 1, 2, 7, 6, 11], [6, 11, 7, 1, 2, 10, 0, 8, 3, 4, 9, 5],
  [7, 6, 11, 5, 4, 10, 4, 2, 10, 4, 0, 2], [3, 4, 8, 3, 5, 4, 3, 2, 5, 10, 5, 2, 11, 7, 6],
  [7, 2, 3, 7, 6, 2, 5, 4, 9], [9, 5, 4, 0, 8, 6, 0, 6, 2, 6, 8, 7],
  [3, 6, 2, 3, 7, 6, 1, 5, 0, 5, 4, 0], [6, 2, 8, 6, 8, 7, 2, 1, 8, 4, 8, 5, 1, 5, 8],
  [9, 5, 4, 10, 1, 6, 1, 7, 6, 1, 3, 7], [1, 6, 10, 1, 7, 6, 1, 0, 7, 8, 7, 0, 9, 5, 4],
  [4, 0, 10, 4, 10, 5, 0, 3, 10, 6, 10, 7, 3, 7, 10], [7, 6, 10, 7, 10, 8, 5, 4, 10, 4, 8, 10],
  [6, 9, 5, 6, 11, 9, 11, 8, 9], [3, 6, 11, 0, 6, 3, 0, 5, 6, 0, 9, 5],
  [0, 11, 8, 0, 5, 11, 0, 1, 5, 5, 6, 11], [6, 11, 3, 6, 3, 5, 5, 3, 1],
  [1, 2, 10, 9, 5, 11, 9, 11, 8, 11, 5, 6], [0, 11, 3, 0, 6, 11, 0, 9, 6, 5, 6, 9, 1, 2, 10],
  [11, 8, 5, 11, 5, 6, 8, 0, 5, 10, 5, 2, 0, 2, 5], [6, 11, 3, 6, 3, 5, 2, 10, 3, 10, 5, 3],
  [5, 8, 9, 5, 2, 8, 5, 6, 2, 3, 8, 2], [9, 5, 6, 9, 6, 0, 0, 6, 2],
  [1, 5, 8, 1, 8, 0, 5, 6, 8, 3, 8, 2, 6, 2, 8], [1, 5, 6, 2, 1, 6],
  [1, 3, 6, 1, 6, 10, 3, 8, 6, 5, 6, 9, 8, 9, 6], [10, 1, 0, 10, 0, 6, 9, 5, 0, 5, 6, 0],
  [0, 3, 8, 5, 6, 10], [10, 5, 6], [11, 5, 10, 7, 5, 11],
  [11, 5, 10, 11, 7, 5, 8, 3, 0], [5, 11, 7, 5, 10, 11, 1, 9, 0],
  [10, 7, 5, 10, 11, 7, 9, 8, 1, 8, 3, 1], [11, 1, 2, 11, 7, 1, 7, 5, 1],
  [0, 8, 3, 1, 2, 7, 1, 7, 5, 7, 2, 11], [9, 7, 5, 9, 2, 7, 9, 0, 2, 2, 11, 7],
  [7, 5, 2, 7, 2, 11, 5, 9, 2, 3, 2, 8, 9, 8, 2], [2, 5, 10, 2, 3, 5, 3, 7, 5],
  [8, 2, 0, 8, 5, 2, 8, 7, 5, 10, 2, 5], [9, 0, 1, 5, 10, 3, 5, 3, 7, 3, 10, 2],
  [9, 8, 2, 9, 2, 1, 8, 7, 2, 10, 2, 5, 7, 5, 2], [1, 3, 5, 3, 7, 5],
  [0, 8, 7, 0, 7, 1, 1, 7, 5], [9, 0, 3, 9, 3, 5, 5, 3, 7],
  [9, 8, 7, 5, 9, 7], [5, 8, 4, 5, 10, 8, 10, 11, 8],
  [5, 0, 4, 5, 11, 0, 5, 10, 11, 11, 3, 0], [0, 1, 9, 8, 4, 10, 8, 10, 11, 10, 4, 5],
  [10, 11, 4, 10, 4, 5, 11, 3, 4, 9, 4, 1, 3, 1, 4], [2, 5, 1, 2, 8, 5, 2, 11, 8, 4, 5, 8],
  [0, 4, 11, 0, 11, 3, 4, 5, 11, 2, 11, 1, 5, 1, 11], [0, 2, 5, 0, 5, 9, 2, 11, 5, 4, 5, 8, 11, 8, 5],
  [9, 4, 5, 2, 11, 3], [2, 5, 10, 3, 5, 2, 3, 4, 5, 3, 8, 4],
  [5, 10, 2, 5, 2, 4, 4, 2, 0], [3, 10, 2, 3, 5, 10, 3, 8, 5, 4, 5, 8, 0, 1, 9],
  [5, 10, 2, 5, 2, 4, 1, 9, 2, 9, 4, 2], [8, 4, 5, 8, 5, 3, 3, 5, 1],
  [0, 4, 5, 1, 0, 5], [8, 4, 5, 8, 5, 3, 9, 0, 5, 0, 3, 5],
  [9, 4, 5], [4, 11, 7, 4, 9, 11, 9, 10, 11], [0, 8, 3, 4, 9, 7, 9, 11, 7, 9, 10, 11],
  [1, 10, 11, 1, 11, 4, 1, 4, 0, 7, 4, 11], [3, 1, 4, 3, 4, 8, 1, 10, 4, 7, 4, 11, 10, 11, 4],
  [4, 11, 7, 9, 11, 4, 9, 2, 11, 9, 1, 2], [9, 7, 4, 9, 11, 7, 9, 1, 11, 2, 11, 1, 0, 8, 3],
  [11, 7, 4, 11, 4, 2, 2, 4, 0], [11, 7, 4, 11, 4, 2, 8, 3, 4, 3, 2, 4],
  [2, 9, 10, 2, 7, 9, 2, 3, 7, 7, 4, 9], [9, 10, 7, 9, 7, 4, 10, 2, 7, 8, 7, 0, 2, 0, 7],
  [3, 7, 10, 3, 10, 2, 7, 4, 10, 1, 10, 0, 4, 0, 10], [1, 10, 2, 8, 7, 4],
  [4, 9, 1, 4, 1, 7, 7, 1, 3], [4, 9, 1, 4, 1, 7, 0, 8, 1, 8, 7, 1],
  [4, 0, 3, 7, 4, 3], [4, 8, 7], [9, 10, 8, 10, 11, 8],
  [3, 0, 9, 3, 9, 11, 11, 9, 10], [0, 1, 10, 0, 10, 8, 8, 10, 11],
  [3, 1, 10, 11, 3, 10], [1, 2, 11, 1, 11, 9, 9, 11, 8],
  [3, 0, 9, 3, 9, 11, 1, 2, 9, 2, 11, 9], [0, 2, 11, 8, 0, 11],
  [3, 2, 11], [2, 3, 8, 2, 8, 10, 10, 8, 9], [9, 10, 2, 0, 9, 2],
  [2, 3, 8, 2, 8, 10, 0, 1, 8, 1, 10, 8], [1, 10, 2],
  [1, 3, 8, 9, 1, 8], [0, 9, 1], [0, 3, 8], []
];

/**
 * Analytic SDF for flat end mill (cylinder)
 */
function flatEndMillSDF(
  px: number, py: number, pz: number,
  cx: number, cy: number, cz: number,
  radius: number, length: number,
  dirX: number, dirY: number, dirZ: number
): number {
  // Transform point to tool local space
  const dx = px - cx;
  const dy = py - cy;
  const dz = pz - cz;

  // Project onto tool axis
  const axialDist = dx * dirX + dy * dirY + dz * dirZ;

  // Radial distance (perpendicular to axis)
  const projX = dx - axialDist * dirX;
  const projY = dy - axialDist * dirY;
  const projZ = dz - axialDist * dirZ;
  const radialDist = Math.sqrt(projX * projX + projY * projY + projZ * projZ);

  // Cylinder SDF: max of radial and axial distances
  const radialSDF = radialDist - radius;
  const axialSDF = Math.max(-axialDist, axialDist - length);

  return Math.max(radialSDF, axialSDF);
}

/**
 * Analytic SDF for ball end mill (hemisphere + cylinder)
 */
function ballEndMillSDF(
  px: number, py: number, pz: number,
  cx: number, cy: number, cz: number,
  radius: number, length: number,
  dirX: number, dirY: number, dirZ: number
): number {
  const dx = px - cx;
  const dy = py - cy;
  const dz = pz - cz;

  const axialDist = dx * dirX + dy * dirY + dz * dirZ;
  const projX = dx - axialDist * dirX;
  const projY = dy - axialDist * dirY;
  const projZ = dz - axialDist * dirZ;
  const radialDist = Math.sqrt(projX * projX + projY * projY + projZ * projZ);

  // Hemisphere at tip (axialDist < radius)
  if (axialDist < radius) {
    const sphereDist = Math.sqrt(radialDist * radialDist + (radius - axialDist) * (radius - axialDist));
    return sphereDist - radius;
  }

  // Cylinder shaft (axialDist >= radius && axialDist <= length)
  if (axialDist <= length) {
    return radialDist - radius;
  }

  // Beyond length
  const dAxial = axialDist - length;
  if (radialDist <= radius) {
    return dAxial;
  }
  return Math.sqrt(dAxial * dAxial + (radialDist - radius) * (radialDist - radius));
}

/**
 * Analytic SDF for bull nose mill (torus corner + cylinder)
 */
function bullNoseMillSDF(
  px: number, py: number, pz: number,
  cx: number, cy: number, cz: number,
  radius: number, cornerR: number, length: number,
  dirX: number, dirY: number, dirZ: number
): number {
  const dx = px - cx;
  const dy = py - cy;
  const dz = pz - cz;

  const axialDist = dx * dirX + dy * dirY + dz * dirZ;
  const projX = dx - axialDist * dirX;
  const projY = dy - axialDist * dirY;
  const projZ = dz - axialDist * dirZ;
  const radialDist = Math.sqrt(projX * projX + projY * projY + projZ * projZ);

  const majorRadius = radius - cornerR;

  // Torus region at tip (axialDist < cornerR)
  if (axialDist < cornerR) {
    const torusCenterDist = Math.sqrt(
      (radialDist - majorRadius) * (radialDist - majorRadius) +
      (cornerR - axialDist) * (cornerR - axialDist)
    );
    return torusCenterDist - cornerR;
  }

  // Cylinder shaft (axialDist >= cornerR && axialDist <= length)
  if (axialDist <= length) {
    return radialDist - radius;
  }

  // Beyond length
  const dAxial = axialDist - length;
  if (radialDist <= radius) {
    return dAxial;
  }
  return Math.sqrt(dAxial * dAxial + (radialDist - radius) * (radialDist - radius));
}

/**
 * Main SDF Engine class for CNC stock simulation
 */
export class SDFEngine {
  private bbox: VoxelBBox;
  private voxelSize: number;

  public resX: number;
  public resY: number;
  public resZ: number;
  public sdfData: Float32Array;

  private originX: number;
  private originY: number;
  private originZ: number;

  constructor(bbox: VoxelBBox, voxelSize: number) {
    this.bbox = bbox;
    this.voxelSize = voxelSize;

    // Compute grid resolution
    this.resX = Math.ceil((bbox.maxX - bbox.minX) / voxelSize) + 1;
    this.resY = Math.ceil((bbox.maxY - bbox.minY) / voxelSize) + 1;
    this.resZ = Math.ceil((bbox.maxZ - bbox.minZ) / voxelSize) + 1;

    this.originX = bbox.minX;
    this.originY = bbox.minY;
    this.originZ = bbox.minZ;

    // Allocate SDF data (initialized to positive infinity = outside)
    const totalVoxels = this.resX * this.resY * this.resZ;
    this.sdfData = new Float32Array(totalVoxels);
    this.sdfData.fill(Number.POSITIVE_INFINITY);
  }

  /**
   * Initialize rectangular stock with proper signed distance field.
   * Negative = inside material, Positive = outside material.
   * Distance is the minimum distance to the bbox boundary.
   */
  public initializeRectangularStock(): void {
    const bMinX = this.bbox.minX;
    const bMaxX = this.bbox.maxX;
    const bMinY = this.bbox.minY;
    const bMaxY = this.bbox.maxY;
    const bMinZ = this.bbox.minZ;
    const bMaxZ = this.bbox.maxZ;

    for (let iz = 0; iz < this.resZ; iz++) {
      const wz = this.originZ + iz * this.voxelSize;
      for (let iy = 0; iy < this.resY; iy++) {
        const wy = this.originY + iy * this.voxelSize;
        for (let ix = 0; ix < this.resX; ix++) {
          const wx = this.originX + ix * this.voxelSize;
          const idx = ix + iy * this.resX + iz * this.resX * this.resY;

          // Distance to each face of the bbox (positive = inside for each face)
          const dx0 = wx - bMinX;
          const dx1 = bMaxX - wx;
          const dy0 = wy - bMinY;
          const dy1 = bMaxY - wy;
          const dz0 = wz - bMinZ;
          const dz1 = bMaxZ - wz;

          // Minimum distance to any face (negative if outside)
          const minDist = Math.min(dx0, dx1, dy0, dy1, dz0, dz1);

          // SDF convention: negative = inside, positive = outside
          this.sdfData[idx] = -minDist;
        }
      }
    }
  }

  /**
   * Remove material at a single point with the given tool
   * Returns number of modified voxels
   */
  public removeMaterial(
    x: number, y: number, z: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): number {
    // Compute tool AABB
    const toolRadius = tool.diameter / 2;
    const toolLength = tool.length;

    const minX = Math.max(0, Math.floor((x - toolRadius - this.originX) / this.voxelSize));
    const maxX = Math.min(this.resX - 1, Math.ceil((x + toolRadius - this.originX) / this.voxelSize));
    const minY = Math.max(0, Math.floor((y - toolRadius - this.originY) / this.voxelSize));
    const maxY = Math.min(this.resY - 1, Math.ceil((y + toolRadius - this.originY) / this.voxelSize));
    const minZ = Math.max(0, Math.floor((z - toolLength - this.originZ) / this.voxelSize));
    const maxZ = Math.min(this.resZ - 1, Math.ceil((z + toolRadius - this.originZ) / this.voxelSize));

    let modifiedCount = 0;

    for (let iz = minZ; iz <= maxZ; iz++) {
      const vz = this.originZ + iz * this.voxelSize;
      for (let iy = minY; iy <= maxY; iy++) {
        const vy = this.originY + iy * this.voxelSize;
        for (let ix = minX; ix <= maxX; ix++) {
          const vx = this.originX + ix * this.voxelSize;
          const idx = ix + iy * this.resX + iz * this.resX * this.resY;

          // Compute tool SDF at this voxel
          let toolSDF: number;
          if (tool.type === 'flat') {
            toolSDF = flatEndMillSDF(vx, vy, vz, x, y, z, toolRadius, toolLength, dirX, dirY, dirZ);
          } else if (tool.type === 'ball') {
            toolSDF = ballEndMillSDF(vx, vy, vz, x, y, z, toolRadius, toolLength, dirX, dirY, dirZ);
          } else if (tool.type === 'bull') {
            toolSDF = bullNoseMillSDF(vx, vy, vz, x, y, z, toolRadius, tool.cornerRadius, toolLength, dirX, dirY, dirZ);
          } else {
            toolSDF = flatEndMillSDF(vx, vy, vz, x, y, z, toolRadius, toolLength, dirX, dirY, dirZ);
          }

          // Material removal: CSG difference = max(stock, -tool)
          // -toolSDF is positive inside the tool, making those voxels "outside"
          const oldSDF = this.sdfData[idx];
          const newSDF = Math.max(oldSDF, -toolSDF);

          if (newSDF !== oldSDF) {
            this.sdfData[idx] = newSDF;
            modifiedCount++;
          }
        }
      }
    }

    return modifiedCount;
  }

  /**
   * Remove material along a swept line segment from A to B
   * Returns number of modified voxels
   */
  public removeSweptMaterial(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): number {
    const toolRadius = tool.diameter / 2;
    const toolLength = tool.length;

    // Compute swept AABB
    const minX = Math.max(0, Math.floor((Math.min(ax, bx) - toolRadius - this.originX) / this.voxelSize));
    const maxX = Math.min(this.resX - 1, Math.ceil((Math.max(ax, bx) + toolRadius - this.originX) / this.voxelSize));
    const minY = Math.max(0, Math.floor((Math.min(ay, by) - toolRadius - this.originY) / this.voxelSize));
    const maxY = Math.min(this.resY - 1, Math.ceil((Math.max(ay, by) + toolRadius - this.originY) / this.voxelSize));
    const minZ = Math.max(0, Math.floor((Math.min(az, bz) - toolLength - this.originZ) / this.voxelSize));
    const maxZ = Math.min(this.resZ - 1, Math.ceil((Math.max(az, bz) + toolRadius - this.originZ) / this.voxelSize));

    const segDx = bx - ax;
    const segDy = by - ay;
    const segDz = bz - az;
    const segLenSq = segDx * segDx + segDy * segDy + segDz * segDz;
    const segLen = Math.sqrt(segLenSq);

    let modifiedCount = 0;

    for (let iz = minZ; iz <= maxZ; iz++) {
      const vz = this.originZ + iz * this.voxelSize;
      for (let iy = minY; iy <= maxY; iy++) {
        const vy = this.originY + iy * this.voxelSize;
        for (let ix = minX; ix <= maxX; ix++) {
          const vx = this.originX + ix * this.voxelSize;
          const idx = ix + iy * this.resX + iz * this.resX * this.resY;

          // Find closest point on segment A->B
          let t = 0;
          if (segLen > 1e-6) {
            const dx = vx - ax;
            const dy = vy - ay;
            const dz = vz - az;
            t = Math.max(0, Math.min(1, (dx * segDx + dy * segDy + dz * segDz) / segLenSq));
          }

          const closestX = ax + t * segDx;
          const closestY = ay + t * segDy;
          const closestZ = az + t * segDz;

          // Compute tool SDF at closest point
          let toolSDF: number;
          if (tool.type === 'flat') {
            toolSDF = flatEndMillSDF(vx, vy, vz, closestX, closestY, closestZ, toolRadius, toolLength, dirX, dirY, dirZ);
          } else if (tool.type === 'ball') {
            toolSDF = ballEndMillSDF(vx, vy, vz, closestX, closestY, closestZ, toolRadius, toolLength, dirX, dirY, dirZ);
          } else if (tool.type === 'bull') {
            toolSDF = bullNoseMillSDF(vx, vy, vz, closestX, closestY, closestZ, toolRadius, tool.cornerRadius, toolLength, dirX, dirY, dirZ);
          } else {
            toolSDF = flatEndMillSDF(vx, vy, vz, closestX, closestY, closestZ, toolRadius, toolLength, dirX, dirY, dirZ);
          }

          // Material removal: CSG difference = max(stock, -tool)
          const oldSDF = this.sdfData[idx];
          const newSDF = Math.max(oldSDF, -toolSDF);

          if (newSDF !== oldSDF) {
            this.sdfData[idx] = newSDF;
            modifiedCount++;
          }
        }
      }
    }

    return modifiedCount;
  }

  /**
   * Generate mesh using Marching Cubes at isosurface 0.0
   */
  public generateMesh(): THREE.BufferGeometry {
    const vertices: number[] = [];
    const indices: number[] = [];

    // Pre-allocate interpolation buffers (12 edges per cube)
    const edgeVertices = new Float32Array(12 * 3);

    for (let iz = 0; iz < this.resZ - 1; iz++) {
      for (let iy = 0; iy < this.resY - 1; iy++) {
        for (let ix = 0; ix < this.resX - 1; ix++) {
          // Get 8 corner values
          const v0 = this.sdfData[ix + iy * this.resX + iz * this.resX * this.resY];
          const v1 = this.sdfData[(ix + 1) + iy * this.resX + iz * this.resX * this.resY];
          const v2 = this.sdfData[(ix + 1) + (iy + 1) * this.resX + iz * this.resX * this.resY];
          const v3 = this.sdfData[ix + (iy + 1) * this.resX + iz * this.resX * this.resY];
          const v4 = this.sdfData[ix + iy * this.resX + (iz + 1) * this.resX * this.resY];
          const v5 = this.sdfData[(ix + 1) + iy * this.resX + (iz + 1) * this.resX * this.resY];
          const v6 = this.sdfData[(ix + 1) + (iy + 1) * this.resX + (iz + 1) * this.resX * this.resY];
          const v7 = this.sdfData[ix + (iy + 1) * this.resX + (iz + 1) * this.resX * this.resY];

          // Compute cube index
          let cubeIndex = 0;
          if (v0 < 0) cubeIndex |= 1;
          if (v1 < 0) cubeIndex |= 2;
          if (v2 < 0) cubeIndex |= 4;
          if (v3 < 0) cubeIndex |= 8;
          if (v4 < 0) cubeIndex |= 16;
          if (v5 < 0) cubeIndex |= 32;
          if (v6 < 0) cubeIndex |= 64;
          if (v7 < 0) cubeIndex |= 128;

          // Skip if completely inside or outside
          if (cubeIndex === 0 || cubeIndex === 255) continue;

          const edgeFlags = MC_EDGE_TABLE[cubeIndex];
          if (edgeFlags === 0) continue;

          // Cube corners in world space
          const x0 = this.originX + ix * this.voxelSize;
          const y0 = this.originY + iy * this.voxelSize;
          const z0 = this.originZ + iz * this.voxelSize;
          const x1 = x0 + this.voxelSize;
          const y1 = y0 + this.voxelSize;
          const z1 = z0 + this.voxelSize;

          // Interpolate edge vertices
          if (edgeFlags & 1) this.interpolateEdge(edgeVertices, 0, x0, y0, z0, x1, y0, z0, v0, v1);
          if (edgeFlags & 2) this.interpolateEdge(edgeVertices, 1, x1, y0, z0, x1, y1, z0, v1, v2);
          if (edgeFlags & 4) this.interpolateEdge(edgeVertices, 2, x1, y1, z0, x0, y1, z0, v2, v3);
          if (edgeFlags & 8) this.interpolateEdge(edgeVertices, 3, x0, y1, z0, x0, y0, z0, v3, v0);
          if (edgeFlags & 16) this.interpolateEdge(edgeVertices, 4, x0, y0, z1, x1, y0, z1, v4, v5);
          if (edgeFlags & 32) this.interpolateEdge(edgeVertices, 5, x1, y0, z1, x1, y1, z1, v5, v6);
          if (edgeFlags & 64) this.interpolateEdge(edgeVertices, 6, x1, y1, z1, x0, y1, z1, v6, v7);
          if (edgeFlags & 128) this.interpolateEdge(edgeVertices, 7, x0, y1, z1, x0, y0, z1, v7, v4);
          if (edgeFlags & 256) this.interpolateEdge(edgeVertices, 8, x0, y0, z0, x0, y0, z1, v0, v4);
          if (edgeFlags & 512) this.interpolateEdge(edgeVertices, 9, x1, y0, z0, x1, y0, z1, v1, v5);
          if (edgeFlags & 1024) this.interpolateEdge(edgeVertices, 10, x1, y1, z0, x1, y1, z1, v2, v6);
          if (edgeFlags & 2048) this.interpolateEdge(edgeVertices, 11, x0, y1, z0, x0, y1, z1, v3, v7);

          // Generate triangles
          const triTable = MC_TRI_TABLE[cubeIndex];
          for (let i = 0; i < triTable.length; i += 3) {
            const baseIdx = vertices.length / 3;

            const e0 = triTable[i];
            const e1 = triTable[i + 1];
            const e2 = triTable[i + 2];

            vertices.push(
              edgeVertices[e0 * 3], edgeVertices[e0 * 3 + 1], edgeVertices[e0 * 3 + 2],
              edgeVertices[e1 * 3], edgeVertices[e1 * 3 + 1], edgeVertices[e1 * 3 + 2],
              edgeVertices[e2 * 3], edgeVertices[e2 * 3 + 1], edgeVertices[e2 * 3 + 2]
            );

            indices.push(baseIdx, baseIdx + 1, baseIdx + 2);
          }
        }
      }
    }

    // Create BufferGeometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Interpolate vertex on edge using SDF values
   */
  private interpolateEdge(
    buffer: Float32Array, edgeIdx: number,
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    val1: number, val2: number
  ): void {
    const offset = edgeIdx * 3;

    // Linear interpolation factor for isosurface = 0.0
    let t = 0.5;
    const denom = val2 - val1;
    if (Math.abs(denom) > 1e-6) {
      t = (0 - val1) / denom;
      t = Math.max(0, Math.min(1, t));
    }

    buffer[offset] = x1 + t * (x2 - x1);
    buffer[offset + 1] = y1 + t * (y2 - y1);
    buffer[offset + 2] = z1 + t * (z2 - z1);
  }

  /**
   * Reset to initial state
   */
  public reset(): void {
    this.sdfData.fill(Number.POSITIVE_INFINITY);
    this.initializeRectangularStock();
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    // TypedArrays are garbage collected automatically
  }

  /**
   * Get statistics about current state
   */
  public getStats(): {
    totalVoxels: number;
    insideVoxels: number;
    surfaceVoxels: number;
    memoryMB: number;
  } {
    let insideCount = 0;
    let surfaceCount = 0;

    for (let i = 0; i < this.sdfData.length; i++) {
      const val = this.sdfData[i];
      if (val < 0) {
        insideCount++;
      } else if (val < this.voxelSize) {
        surfaceCount++;
      }
    }

    return {
      totalVoxels: this.sdfData.length,
      insideVoxels: insideCount,
      surfaceVoxels: surfaceCount,
      memoryMB: (this.sdfData.byteLength) / (1024 * 1024)
    };
  }
}

/**
 * Factory function to create SDF engine
 */
export function createSDFEngine(bbox: VoxelBBox, voxelSize: number): SDFEngine {
  const engine = new SDFEngine(bbox, voxelSize);
  engine.initializeRectangularStock();
  return engine;
}
